import type { ComponentProps } from 'react';
import { forwardRef } from 'react';
import type { Token } from '../../../styled-system/tokens';
import { token } from '../../../styled-system/tokens';
import { css, cx } from '../../../styled-system/css';
import Badge from '../../assets/icons/badge.svg?react';
import CheckFillOff from '../../assets/icons/check_fill_off.svg?react';
import Close from '../../assets/icons/close.svg?react';
import ContentCopy from '../../assets/icons/content_copy.svg?react';
import ErrorFillOn from '../../assets/icons/error_fill_on.svg?react';
import ErrorFillOff from '../../assets/icons/error_fill_off.svg?react';
import Lock from '../../assets/icons/lock.svg?react';
import Apps from '../../assets/icons/apps.svg?react';
import NavigateNext from '../../assets/icons/navigate_next.svg?react';
import HelpFillOff from '../../assets/icons/help_fill_off.svg?react';
import ExpandLess from '../../assets/icons/expand_less.svg?react';
import ExpandMore from '../../assets/icons/expand_more.svg?react';
import OpenInNew from '../../assets/icons/open_in_new.svg?react';
import ArrowRight from '../../assets/icons/arrow_right.svg?react';

export const ICON_NAMES = [
  'badge',
  'check_fill_off',
  'close',
  'content_copy',
  'error_fill_on',
  'error_fill_off',
  'lock',
  'apps',
  'navigate_next',
  'help_fill_off',
  'expand_less',
  'expand_more',
  'open_in_new',
  'arrow_right'
] as const;

type IconName = (typeof ICON_NAMES)[number];

type BaseIconProps = { name: IconName; color?: Token; size: Size };

type IconProps = ComponentProps<'span'> & BaseIconProps;

type IconButtonProps = ComponentProps<'button'> & BaseIconProps;

type Size = 'l' | 'm' | 's';

type IconSize = {
  [key in Size]: number;
};

/**
 * アイコンボタン - hover時のサイズ
 * @see https://www.figma.com/design/ftgyCtqHVci9BMgFZPTnwT/ZEN-ID---%E3%83%87%E3%82%B6%E3%82%A4%E3%83%B3%E3%82%AC%E3%82%A4%E3%83%89?node-id=804-5478&m=dev
 */
const clickableIconSize: IconSize = {
  l: 48,
  m: 40,
  s: 32,
};

/**
 * アイコンボタン - hover時のサイズから余白を抜いたサイズ
 * @see https://www.figma.com/design/ftgyCtqHVci9BMgFZPTnwT/ZEN-ID---%E3%83%87%E3%82%B6%E3%82%A4%E3%83%B3%E3%82%AC%E3%82%A4%E3%83%89?node-id=804-5478&m=dev
 */
const iconSize: IconSize = {
  l: 32,
  m: 24,
  s: 16,
};

/**
 * アイコン両サイド余白(px)
 */
const MARGIN_ON_BOTH_SIDES_ICON = 16;

const svgStyleProps = css({
  fill: 'currentColor',
  '& > g': { '& > rect': { fill: 'inherit' } },
  '& > path': { fill: 'inherit' },
});

const hoverStyle = css({
  _hover: {
    borderRadius: '{radii.full}',
    background: '{colors.hover.light-blue}',
    cursor: 'pointer',
    _disabled: {
      borderRadius: 'none',
      background: 'none',
      cursor: 'default',
    },
  },
});

/**
 * 表示目的に使用する通常のアイコンコンポーネント
 * @see https://github.com/pd4d10/vite-plugin-svgr/issues/84
 */
export function Icon({ className, name, color = 'colors.text.main', size, ...props }: IconProps) {
  const Commponet = getSvgByName(name);
  return (
    <span
      className={className}
      style={{ color: token(color), width: `${iconSize[size]}px`, height: `${iconSize[size]}px` }}
      {...props}
    >
      <Commponet className={svgStyleProps} width={iconSize[size]} height={iconSize[size]} />
    </span>
  );
}

// forwardRefを使用している理由
// forwardRefを使用しない場合、自コンポーネント内部でbuttonを使っている都合上、radixコンポーネントで提供されているTriggerと組み合わせるとうまく機能しない。
// そのため、forwardRefを使用してbuttonの参照を受け取れるようにしている
// https://github.com/radix-ui/primitives/issues/1924#issuecomment-1412842980
/**
 * クリック可能なアイコンコンポーネント
 */
export const ClickableIcon = forwardRef<HTMLButtonElement, IconButtonProps>(function ClickableIcon(
  { className, name, color = 'colors.text.main', size, disabled = false, ...props },
  ref
) {
  const Commponet = getSvgByName(name);
  return (
    <button
      ref={ref}
      className={cx(className, hoverStyle)}
      style={{
        color: disabled ? token('colors.text.disabled') : token(color),
        width: `${clickableIconSize[size]}px`,
        height: `${clickableIconSize[size]}px`,
        padding: token('spacing.8'),
      }}
      disabled={disabled}
      {...props}
    >
      {/* アイコン自体の大きさは両サイド余白を抜いた大きさにする */}
      <Commponet
        className={svgStyleProps}
        width={clickableIconSize[size] - MARGIN_ON_BOTH_SIDES_ICON}
        height={clickableIconSize[size] - MARGIN_ON_BOTH_SIDES_ICON}
      />
    </button>
  );
});

function getSvgByName(name: IconName) {
  switch (name) {
    case 'badge':
      return Badge;
    case 'check_fill_off':
      return CheckFillOff;
    case 'close':
      return Close;
    case 'content_copy':
      return ContentCopy;
    case 'error_fill_on':
      return ErrorFillOn;
    case 'error_fill_off':
      return ErrorFillOff;
    case 'lock':
      return Lock;
    case 'apps':
      return Apps;
    case 'navigate_next':
      return NavigateNext;
    case 'help_fill_off':
      return HelpFillOff;
    case 'expand_less':
      return ExpandLess;
    case 'expand_more':
      return ExpandMore;
    case 'open_in_new':
      return OpenInNew;
    case 'arrow_right':
      return ArrowRight;
    default:
      throw new Error(name satisfies never);
  }
}
